import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { JobVacancyPhaseTypeEnumLabels } from 'src/app/enums/job-vacancy-phase-type.enum';
import { JobVacancy, JobVacancyPhase } from 'src/app/models/job_vacancy.model';
import { ApiService } from 'src/app/services';

export interface JobVacancyPhasesConfigDialogData {
  job_vacancy: JobVacancy;
}

type JobVacancyPhaseWithTypeLabel = JobVacancyPhase & { type_label: string };

@Component({
  selector: 'app-job-vacancy-phases-config-dialog',
  templateUrl: './job-vacancy-phases-config-dialog.component.html',
  styleUrl: './job-vacancy-phases-config-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
  ],
})
export class JobVacancyPhasesConfigDialogComponent implements OnInit {
  private readonly dialog_ref = inject(MatDialogRef<JobVacancyPhasesConfigDialogComponent, boolean>);
  private readonly api_service = inject(ApiService);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);
  private readonly change_detector_ref = inject(ChangeDetectorRef);

  readonly data = inject<JobVacancyPhasesConfigDialogData>(MAT_DIALOG_DATA);

  readonly loading = signal(false);
  readonly sorted_phases = signal<JobVacancyPhaseWithTypeLabel[]>([]);

  readonly phases_form = this.form_builder.group({
    phases: this.form_builder.array([]),
  });

  ngOnInit(): void {
    const phases_with_labels = [...(this.data.job_vacancy.job_vacancy_phases ?? [])]
      .sort((first_phase, second_phase) => first_phase.sort_order - second_phase.sort_order)
      .map(job_vacancy_phase => ({
        ...job_vacancy_phase,
        type_label: JobVacancyPhaseTypeEnumLabels[job_vacancy_phase.type] ?? job_vacancy_phase.type,
      }));

    this.sorted_phases.set(phases_with_labels);
    phases_with_labels.forEach(job_vacancy_phase => {
      this.phases_form_array.push(this.buildPhaseFormGroup(job_vacancy_phase));
    });
  }

  get phases_form_array(): FormArray {
    return this.phases_form.get('phases') as FormArray;
  }

  movePhaseUp(index: number): void {
    if (index <= 0) {
      return;
    }

    this.movePhase(index, index - 1);
  }

  movePhaseDown(index: number): void {
    if (index >= this.sorted_phases().length - 1) {
      return;
    }

    this.movePhase(index, index + 1);
  }

  save(): void {
    if (this.loading() || this.phases_form.invalid) {
      return;
    }

    this.loading.set(true);

    const phase_updates = this.phases_form_array.controls.map((phase_control, sort_order) => {
      const raw = (phase_control as FormGroup).getRawValue();

      return this.api_service.updateJobVacancyPhase(raw.id, {
        name: raw.name,
        sort_order,
        recommended_min_input_score: this.parseOptionalScore(raw.recommended_min_input_score),
        recommended_max_input_score: this.parseOptionalScore(raw.recommended_max_input_score),
        recommended_min_output_score: this.parseOptionalScore(raw.recommended_min_output_score),
        recommended_max_output_score: this.parseOptionalScore(raw.recommended_max_output_score),
      });
    });

    forkJoin(phase_updates)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          this.dialog_ref.close(true);
        },
        error: (err: { error?: { error?: string } }): void => {
          this.loading.set(false);
          const message = err?.error?.error ?? 'Error al guardar las fases.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
          this.change_detector_ref.markForCheck();
        },
      });
  }

  cancel(): void {
    this.dialog_ref.close(false);
  }

  private movePhase(from_index: number, to_index: number): void {
    const reordered_phases = [...this.sorted_phases()];
    const moved_phase = reordered_phases[from_index];
    reordered_phases.splice(from_index, 1);
    reordered_phases.splice(to_index, 0, moved_phase);
    this.sorted_phases.set(reordered_phases);

    const moved_control = this.phases_form_array.at(from_index);
    this.phases_form_array.removeAt(from_index);
    this.phases_form_array.insert(to_index, moved_control);

    this.change_detector_ref.markForCheck();
  }

  private buildPhaseFormGroup(job_vacancy_phase: JobVacancyPhase) {
    return this.form_builder.group({
      id: [job_vacancy_phase.id],
      type: [job_vacancy_phase.type],
      name: [job_vacancy_phase.name, [Validators.required, Validators.maxLength(255)]],
      recommended_min_input_score: [job_vacancy_phase.recommended_min_input_score],
      recommended_max_input_score: [job_vacancy_phase.recommended_max_input_score],
      recommended_min_output_score: [job_vacancy_phase.recommended_min_output_score],
      recommended_max_output_score: [job_vacancy_phase.recommended_max_output_score],
    });
  }

  private parseOptionalScore(value: number | string | null): number | null {
    if (value === null || value === '') {
      return null;
    }

    return Number(value);
  }
}
